import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'

/**
 * Opposition Command — the module shell.
 *
 * PollSphere's seven tab ids are unchanged, per the port rule in the plan: the vocabulary is
 * the product's identity and renaming it would make the port a redesign. "Open a target" is
 * the one addition — PollSphere has no equivalent because it has no public-figure gate, and
 * without a screen the gate was enforceable but unreachable.
 *
 * A tab the caller lacks the authority for is filtered out. Note what that does NOT mean:
 * close-held targets are omitted by the SERVER from every list, so a caller without
 * `opposition:close-hold` sees the tabs and simply never sees those rows — the existence of
 * an operation against a named person is itself the sensitive fact.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

export const OPPOSITION_TABS: Tab[] = [
  { to: '/opposition', label: 'Threat Matrix', end: true,
    hint: 'Electability against vulnerability, one bubble per target' },
  { to: '/opposition/new', label: 'Open a target',
    hint: 'Put a candidate, party or organisation under command',
    permission: 'opposition:target' },
  { to: '/opposition/live-feed', label: 'Live Survey Feed',
    hint: 'Our share against the field, with base-n and margin beside every figure' },
  { to: '/opposition/surfaces', label: 'Vulnerability Surfaces',
    hint: 'The documented exposures, and the evidence behind each' },
  { to: '/opposition/scandals', label: 'Scandals & Legal',
    hint: 'Cross-target integrity roll-up, strongest evidence first' },
  // Renamed from "Kill-Chain Operations". The feature is ordinary opposition research with
  // an evidence floor and a legal-clearance gate; the military name made it read as something
  // else entirely, to a candidate and to anybody who saw a screenshot. See ./stages.
  { to: '/opposition/kill-chain', label: 'Research in progress',
    hint: 'Where each opponent file has got to, and what the next step needs' },
  { to: '/opposition/operations', label: 'Active Operations',
    hint: 'What is running, who is accountable, what it has cost',
    permission: 'opposition:operate' },
  { to: '/opposition/watchlist', label: 'Watch List',
    hint: 'Ranked by CHANGE, not by level' },
]

export function OppositionLayout() {
  const authorities = useSession((s) => s.authorities)
  const tabs = OPPOSITION_TABS.filter((t) => hasPermission(authorities, t.permission))

  return (
    <div className="space-y-3">
      <nav className="axis-table-scroll flex gap-0.5 border-b border-border"
           aria-label="Opposition Command">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            title={tab.hint}
            className={({ isActive }) => cn(
              'shrink-0 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[12px]',
              isActive
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground')}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
